import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, distinct, mergeMap, last, map, concatMap } from 'rxjs/operators';
import { concat } from 'cypress/types/lodash';
import * as ExoActions from './actions';
import { SafeAttachmentPolicy } from './model';
import { retry } from 'projects/angular-clarity/src/app/pipes/retry.pipe';
import { TenantAjaxService } from 'projects/angular-clarity/src/app/services/ajax/tenant-ajax.service';

const EndPoint = '/api/microsoft/powershell/exo/safe-attachment-policy';

@Injectable()
export class SafeAttachmentPolicyEffects {
    fetchSafeAttachmentPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExoActions.loadSafeAttachmentPolicy),
            distinct((action) => action._tenant),
            mergeMap(({ _tenant }) =>
                this.get(_tenant).pipe(
                    retry(3000, 3, 'safe attachment policy timeout'),
                    last(),
                    map((data: SafeAttachmentPolicy[]) =>
                        ExoActions.loadSafeAttachmentPolicySuccess({ _tenant, data }),
                    ),
                    catchError((error) => of(ExoActions.loadSafeAttachmentPolicyFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    createSafeAttachmentPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExoActions.createExoSafeAttachmentPolicy),
            mergeMap((action) =>
                this.post(action._tenant, action.data).pipe(
                    map((res) => ({ ...res[0] })),
                    map((data: SafeAttachmentPolicy) =>
                        ExoActions.createExoSafeAttachmentPolicySuccess({ _tenant: action._tenant, data }),
                    ),
                    catchError((error) =>
                        of(
                            ExoActions.createExoSafeAttachmentPolicyFailure({
                                _tenant: action._tenant,
                                error: { message: error.message, trace: error.trace },
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateSafeAttachmentPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExoActions.updateExoSafeAttachmentPolicy),
            mergeMap((action) =>
                this.put(action._tenant, action.data).pipe(
                    map((res) => ({ ...res[0] })),
                    map((data: SafeAttachmentPolicy) =>
                        ExoActions.updateExoSafeAttachmentPolicySuccess({ _tenant: action._tenant, data }),
                    ),
                    catchError((error) =>
                        of(
                            ExoActions.updateExoSafeAttachmentPolicyFailure({
                                _tenant: action._tenant,
                                error: { message: error.message, trace: error.trace },
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    private get(tenant: string) {
        return this.ajax.get(tenant, EndPoint);
    }

    private post(tenant: string, body: Partial<SafeAttachmentPolicy>): Observable<SafeAttachmentPolicy[]> {
        return this.ajax.post(tenant, EndPoint, body);
    }

    private put(tenant: string, body: Partial<SafeAttachmentPolicy>): Observable<SafeAttachmentPolicy[]> {
        return this.ajax.put(tenant, `${EndPoint}`, body);
    }

    private delete(tenant: string, identity: string) {
        return this.ajax.delete(tenant, `${EndPoint}/${identity}`);
    }

    constructor(
        private actions$: Actions,
        private ajax: TenantAjaxService,
    ) {}
}
